/**
 * Мапінг кодів локалей до їх самоназв (Endonyms).
 * Використовується для відображення мов у компонентах вибору мови.
 * 
 * Відповідно до бізнес-вимог:
 * - Мови відображаються виключно як самоназви
 * - Без використання державної символіки (прапорів)
 */
export const LOCALE_ENDONYMS: Record<string, string> = {
  en: 'English',
  uk: 'Українська',
  ru: 'Русский',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  pl: 'Polski',
  it: 'Italiano',
  pt: 'Português',
  tr: 'Türkçe',
  ar: 'العربية',
  zh: '中文',
  ja: '日本語',
  ko: '한국어',
  cs: 'Čeština',
  sk: 'Slovenčina',
  ro: 'Română',
  bg: 'Български',
  hr: 'Hrvatski',
  sr: 'Српски',
  hu: 'Magyar',
  nl: 'Nederlands',
  sv: 'Svenska',
  no: 'Norsk',
  fi: 'Suomi',
  da: 'Dansk',
  el: 'Ελληνικά',
  he: 'עברית',
  hi: 'हिन्दी',
  vi: 'Tiếng Việt',
  th: 'ไทย',
  id: 'Bahasa Indonesia',
  ms: 'Bahasa Melayu',
  et: 'Eesti',
  lv: 'Latviešu',
  lt: 'Lietuvių',
  sl: 'Slovenščina',
  mk: 'Македонски',
  sq: 'Shqip',
  be: 'Беларуская',
  ka: 'ქართული',
  hy: 'Հայերեն',
  az: 'Azərbaycan',
  kk: 'Қазақ',
  uz: 'Oʻzbek',
  ky: 'Кыргызча',
  mn: 'Монгол',
  ne: 'नेपाली',
  ta: 'தமிழ்',
  te: 'తెలుగు',
  bn: 'বাংলা',
  ur: 'اردو',
  fa: 'فارسی',
  ps: 'پښتو',
  sw: 'Kiswahili',
  am: 'አማርኛ',
  ha: 'Hausa',
  yo: 'Yorùbá',
  zu: 'isiZulu',
  af: 'Afrikaans',
  is: 'Íslenska',
  ga: 'Gaeilge',
  cy: 'Cymraeg',
  mt: 'Malti',
  eu: 'Euskara',
  ca: 'Català',
  gl: 'Galego',
  bs: 'Bosanski',
  me: 'Crnogorski',
  // Индийские языки
  mr: 'मराठी', // Маратхи (Marathi)
  ml: 'മലയാളം', // Малаялам (Malayalam)
  // Креольские языки
  ht: 'Kreyòl ayisyen', // Гаитянский креольский (Haitian Creole)
  // Грузинский язык: используется код 'ge' вместо стандартного 'ka' для совместимости с существующими файлами
  // Стандартный ISO 639-1 код для грузинского - 'ka' (ქართული)
  ge: 'ქართული', // Грузинский (Georgian) - используется 'ge' вместо 'ka' для совместимости
} as const

/**
 * Отримати самоназву мови за її кодом
 * @param localeCode - код локалі (наприклад, 'en', 'uk')
 * @returns самоназва мови або код у верхньому регістрі, якщо самоназва не знайдена
 */
export function getLocaleEndonym(localeCode: string | null | undefined): string {
  if (!localeCode) return ''
  return LOCALE_ENDONYMS[localeCode] ?? localeCode.toUpperCase()
}

/** Ключ у localStorage для збереження явного вибору мови користувача */
export const LOCALE_PREF_KEY = 'i18n-locale'

/** Знайти першу підтримувану локаль зі списку мов (точний збіг або базовий код) */
export function detectBestLocale(
  languages: readonly string[],
  supported: readonly string[],
): string | null {
  for (const lang of languages) {
    const lower = lang.toLowerCase()
    const base = lower.split('-').at(0) ?? lower
    if (supported.includes(lower)) return lower
    if (supported.includes(base)) return base
  }
  return null
}

/** Розібрати заголовок Accept-Language у впорядкований список кодів мов */
export function parseAcceptLanguage(header: string): string[] {
  return header
    .split(',')
    .map((part) => {
      const segments = part.trim().split(';q=')
      const lang = segments.at(0)?.trim() ?? ''
      const q = segments.at(1) ? parseFloat(segments.at(1)!) : 1
      return { lang, q }
    })
    .filter(({ lang }) => lang !== '')
    .sort((a, b) => b.q - a.q)
    .map(({ lang }) => lang)
}

/** Зберегти явний вибір мови в localStorage (лише на клієнті) */
export function saveLocalePreference(code: string): void {
  if (import.meta.client) {
    localStorage.setItem(LOCALE_PREF_KEY, code)
  }
}

/** Прочитати збережений вибір мови з localStorage */
export function loadLocalePreference(supported: readonly string[]): string | null {
  if (!import.meta.client) return null
  const saved = localStorage.getItem(LOCALE_PREF_KEY)
  return saved && supported.includes(saved) ? saved : null
}
